import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Gates a question's answer UI on its stimulus actually being on screen.
 *
 * Choice buttons submit the response on click, so until the concept image has painted a
 * respondent can register an answer to packaging they never saw — a silent data-quality hole
 * (they rate a stimulus they did not see, and nothing about the saved response looks wrong).
 *
 * Only still images gate: videos stream progressively and have their own controls, so waiting
 * on them would block the respondent indefinitely. The gate always fails OPEN — a broken or
 * slow image must never trap someone mid-survey.
 *
 * SCOPED, NOT RESET. The caller passes the identity of "this question, this time" — a visit key,
 * not a question id. Everything the gate learns is stamped with the scope it was learned in and
 * read back only for the scope on screen, so a new visit starts locked BY DERIVATION, in the frame
 * it first paints. A backward `skip_to` re-enters a question whose url list is character-for-
 * character the one already resolved, and whose fail-open may already have fired; keyed on the
 * question alone, both open the answer UI over an <img> that has just been remounted and has not
 * painted a pixel. Resetting from a `useEffect` cannot close that: effects are passive, so the
 * reset lands one render AFTER the frame that needed it — the same argument the MaxDiff set cursor
 * and the option-media paint keys are derived for.
 */

/** Media that streams rather than loading as a single frame; never gates the answer UI. */
const VIDEO_URL_PATTERN = /(youtube\.com|youtu\.be|vimeo\.com|\.(?:mp4|mov|avi|webm|mkv)(?:\?|$))/i;

/** Fail-open backstop: if a stimulus never resolves, unlock rather than strand the respondent. */
export const STIMULUS_LOAD_TIMEOUT_MS = 8000;

export const isVideoUrl = (url: string): boolean => VIDEO_URL_PATTERN.test(url);

/** What the gate has learned, and the scope it learned it in. */
type StimulusProgress = {
  /** `${visitKey}#${imageKey}`; null belongs to no visit, so every scope reads it as empty. */
  scope: string | null;
  resolved: Set<string>;
  timedOut: boolean;
};

const NO_STIMULUS_PROGRESS: StimulusProgress = { scope: null, resolved: new Set(), timedOut: false };

export interface StimulusReady {
  /** False only while a still image for this visit is still in flight. */
  ready: boolean;
  /** Call on load OR error — an image that failed is "resolved", it just isn't visible. */
  markResolved: (url: string) => void;
  /** How many images the gate is waiting on (0 = nothing to gate). */
  gatedImageCount: number;
}

export function useStimulusReady(
  /** The visit this stimulus belongs to (`makeQuestionVisitKey`), never a bare question id. */
  visitKey: string | number | null | undefined,
  mediaUrls: string[] | undefined | null,
): StimulusReady {
  const imageUrls = useMemo(
    () => (mediaUrls ?? []).filter((url) => Boolean(url) && !isVideoUrl(url)),
    [mediaUrls],
  );
  // Join, not the array identity: a re-render that rebuilds an equivalent list must not re-arm
  // the gate and re-lock the buttons under the respondent.
  const imageKey = imageUrls.join('|');
  // The image list is part of the scope as well as the visit, because an adaptive question can
  // swap its stimulus without the respondent moving — and the new one has not painted either.
  const scope = `${visitKey ?? ''}#${imageKey}`;

  const [progress, setProgress] = useState<StimulusProgress>(NO_STIMULUS_PROGRESS);
  const scoped = progress.scope === scope ? progress : NO_STIMULUS_PROGRESS;

  useEffect(() => {
    if (imageKey === '') return;
    const timer = setTimeout(
      () => setProgress((previous) =>
        previous.scope === scope
          ? { ...previous, timedOut: true }
          : { scope, resolved: new Set(), timedOut: true }),
      STIMULUS_LOAD_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [scope, imageKey]);

  const markResolved = useCallback((url: string) => {
    setProgress((previous) => {
      if (previous.scope !== scope) return { scope, resolved: new Set([url]), timedOut: false };
      if (previous.resolved.has(url)) return previous;
      return { ...previous, resolved: new Set(previous.resolved).add(url) };
    });
  }, [scope]);

  const ready =
    imageUrls.length === 0 || scoped.timedOut || imageUrls.every((url) => scoped.resolved.has(url));

  return { ready, markResolved, gatedImageCount: imageUrls.length };
}
